import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import cn from "classnames";
import {
  CalendarIcon,
  DownloadIcon,
  Flag,
  Gauge,
  Monitor,
  Tag,
  Users,
  UserCircle,
  X,
  Plus,
  BookOpen,
  Menu,
  RefreshCw
} from "lucide-react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton
} from "@material-ui/core";
import LoadingWrapper from "components/LoadingWrapper";
import { isMobileDevice } from "utils/isMobile";
import types from "../../constants/types";
import styles from "./SidebarV2.module.scss";
import Tour from "reactour";
import { useSelector } from "react-redux";
import { selectGuideListByKey } from "stores/config/config.selector";
import { getIsGuideByKey, GUIDE_TYPE_ENUM, setIsGuideByKey } from "utils/helper";

export default function SidebarView(props) {
  const history = useHistory();
  const { t } = useTranslation();
  const { onMenu, getMenu, onDehaze, dehaze } = props;
  const [activeMenu, setActiveMenu] = useState(null);
  const [openPopupCreateCampaign, setOpenPopupCreateCampaign] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openMobileDialog, setOpenMobileDialog] = useState(false);

  // Tour onboarding cho menu navigation
  const tourOverview = useSelector(selectGuideListByKey(GUIDE_TYPE_ENUM.overview));
  const [tourConfig, setTourConfig] = useState(null);

  const menu = [
    {
      name: t(types.MENU.HOME.NAME),
      icon: <Monitor size={16} />,
      link: types.MENU.HOME.LINK,
      id: "overview"
    },
    {
      name: t(types.MENU.FANPAGE.NAME),
      icon: <Flag size={16} />,
      id: "fanpage_manager",
      link: types.MENU.FANPAGE.LINK
    },
    {
      name: t(types.MENU.CUSTOMER.NAME),
      icon: <Users size={16} />,
      id: "customer_manager",
      link: types.MENU.CUSTOMER.LINK
    },
    {
      name: t(types.MENU.TAG.NAME),
      icon: <Tag size={16} />,
      id: "tag_manager",
      link: types.MENU.TAG.LINK
    },
    {
      name: t(types.MENU.CAMPAGIGN.NAME),
      icon: <Gauge size={16} />,
      id: "campaign_manager",
      link: types.MENU.CAMPAGIGN.LINK
    },

    {
      name: t(types.MENU.SETTING_ACCOUNT.NAME),
      icon: <UserCircle size={16} />,
      id: "setting_account",
      link: types.MENU.SETTING_ACCOUNT.LINK
    }
  ];

  useEffect(() => {
    const url_string = window.location.href;
    const url = new URL(url_string);
    const pathname = url.pathname;
    setActiveMenu(pathname);
  }, []);

  // Load tour steps cho menu navigation
  useEffect(() => {
    if (getIsGuideByKey(GUIDE_TYPE_ENUM.overview) && tourOverview?.length) {
      console.log('[SidebarView] tour overview steps RAW:', JSON.stringify(tourOverview))
      // Override selector + content nếu cần
      const stepOverrides = {
        '#overview':         { selector: '#overview',         content: t('sidebar.tour_overview') },
        '#fanpage_manager':  { selector: '#fanpage_manager',  content: t('sidebar.tour_fanpage') },
        '#customer_manager': { selector: '#customer_manager', content: t('sidebar.tour_customer') },
        '#tag_manager':      { selector: '#tag_manager',      content: t('sidebar.tour_tag') },
        '#campaign_manager': { selector: '#campaign_manager', content: t('sidebar.tour_campaign') },
        '#setting_account':  { selector: '#setting_account',  content: t('sidebar.tour_account') },
        '#upgrade':          { selector: '#upgrade',          content: t('sidebar.tour_upgrade') },
      }
      
      const mappedSteps = tourOverview.map((step) => {
        const override = stepOverrides[step.selector]
        if (override) {
          console.log(`[SidebarView] Override step: ${step.selector}`, override)
          return { ...step, ...override }
        }
        console.log(`[SidebarView] Keep original step:`, step.selector)
        return step
      })
      
      // THÊM 2 steps thiếu: tag_manager và setting_account
      // Tìm vị trí sau customer_manager (index 2) để chèn tag_manager
      const customerIndex = mappedSteps.findIndex(s => s.selector === '#customer_manager')
      if (customerIndex !== -1 && !mappedSteps.find(s => s.selector === '#tag_manager')) {
        mappedSteps.splice(customerIndex + 1, 0, {
          selector: '#tag_manager',
          content: t('sidebar.tour_tag')
        })
        console.log('[SidebarView] ADDED missing step: #tag_manager')
      }
      
      // Tìm vị trí sau campaign_manager để chèn setting_account
      const campaignIndex = mappedSteps.findIndex(s => s.selector === '#campaign_manager')
      if (campaignIndex !== -1 && !mappedSteps.find(s => s.selector === '#setting_account')) {
        mappedSteps.splice(campaignIndex + 1, 0, {
          selector: '#setting_account',
          content: t('sidebar.tour_account')
        })
        console.log('[SidebarView] ADDED missing step: #setting_account')
      }
      
      console.log('[SidebarView] tour overview steps FINAL:', JSON.stringify(mappedSteps))
      setTourConfig(mappedSteps)
      setIsGuideByKey(GUIDE_TYPE_ENUM.overview)
    }
  }, [tourOverview]);

  const handleMenu = (item) => {
    if (isMobileDevice() && item.id !== "overview") {
      setOpenMobileDialog(true);
      return;
    }
    history.push(item.link);
  };

  const handleClickTutorial = (url) => {
    window.open(url, "_blank");
  };

  const handleClickExtension = (url) => {
    if (isMobileDevice()) {
      setOpenMobileDialog(true);
      return;
    }
    window.open(url, "_blank");
    setOpenPopupCreateCampaign(false);
  };

  const handleSubmitOpenExtension = () => {
    window.open(
      types.URL.FACEBOOK + `&token=${localStorage.getItem("token") || null}`,
      "_blank"
    );
    setOpenPopupCreateCampaign(false);
  };

  const handleDehazeIcon = () => {
    onDehaze(!dehaze);
  };

  const handleToggleCreateCampaign = () => {
    if (isMobileDevice()) {
      setOpenMobileDialog(true);
      return;
    }
    setOpenPopupCreateCampaign(!openPopupCreateCampaign);
  };

  return (
    <>
      <div className={cn(styles.sidebar, { [styles.collapsed]: dehaze })}>
        {/* ── SIDEBAR TOP: Logo + Toggle ── */}
        <div className={styles.sidebarTop}>
          <a
            className={styles.logoWrap}
            href="https://quantri.bombot.vn"
            target="_blank"
            rel="noreferrer"
          >
            <img src="/icon.png" alt="Bombot" className={styles.logoBox} />
            <img src="/logo.png" alt="Bombot" className={styles.logoName} />
          </a>
          <button
            className={styles.sidebarToggle}
            onClick={handleDehazeIcon}
            title="Thu gọn / Mở rộng"
          >
            <Menu size={17} />
          </button>
        </div>

        {/* ── NAV ITEMS ── */}
        <nav className={styles.sidebarNav}>
          {menu.map((item, index) => (
            <button
              key={index}
              id={item.id}
              className={cn(styles.sideItem, {
                [styles.active]: activeMenu === item.link
              })}
              onClick={() => handleMenu(item)}
            >
              {item.icon}
              <span>{item.name}</span>
            </button>
          ))}
        </nav>

        {/* ── BOTTOM ACTIONS ── */}
        <div className={styles.sidebarBottom}>
          <button
            className={cn(styles.btnSide, styles.btnSidePrimary)}
            onClick={handleToggleCreateCampaign}
          >
            <Plus size={14} />
            <span>{t("common.btn.create_campaign")}</span>
          </button>
          <button
            className={cn(styles.btnSide, styles.btnSideSecondary)}
            onClick={() => handleClickTutorial(types.URL.TUTORIAL)}
          >
            <BookOpen size={14} />
            <span>{t("common.btn.user_manual")}</span>
          </button>
          <button
            className={cn(styles.btnSide, styles.btnSideDanger)}
            onClick={() => handleClickExtension(types.URL.EXTENSION)}
          >
            <DownloadIcon size={14} />
            <span>{t("common.btn.download_file")}</span>
          </button>
        </div>
      </div>

      {/* ── CREATE CAMPAIGN DIALOG ── */}
      <Dialog
        open={openPopupCreateCampaign}
        maxWidth="xs"
        classes={{ paper: styles.dialog }}
      >
        <LoadingWrapper isOpen={isLoading}>
          {/* Close button */}
          <IconButton
            aria-label="close"
            className={styles.closeButton}
            onClick={handleToggleCreateCampaign}
            style={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}
          >
            <X size={16} />
          </IconButton>

          {/* Icon + Title + Body */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 22px 0' }}>
            <div style={{
              width: 52, height: 52, borderRadius: '50%',
              background: '#DCFCE7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 12,
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 26, height: 26 }}>
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 12l10 5 10-5"/>
                <path d="M2 17l10 5 10-5"/>
              </svg>
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 10, textAlign: 'center' }}>
              {t("customer_page.ext_check_title")}
            </div>
            <p style={{ fontSize: 13, lineHeight: 1.7, color: '#6B7280', textAlign: 'center', margin: '0 0 24px' }}>
              {t("common.btn.instruction1")}{" "}
              <a
                href="https://hotro.bombot.vn/knowledgebase/huong-dan-cai-dat-phien-ban-moi-ung-dung-gui-tin-hang-loat-bombot-vn/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#16A34A', fontWeight: 600, textDecoration: 'none' }}
                onClick={() => setOpenPopupCreateCampaign(false)}
              >
                {t("common.btn.see_detail")}
              </a>{" "}
              {t("common.btn.instruction2")}
            </p>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, padding: '0 28px 28px' }}>
            <button
              onClick={() => handleClickExtension(types.URL.EXTENSION)}
              style={{
                flex: 1, padding: '10px 0',
                border: '1px solid #E5E7EB', background: '#fff',
                borderRadius: 8, fontSize: 13, fontWeight: 600,
                color: '#6B7280', cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                transition: 'border-color 0.12s, color 0.12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#22C55E'; e.currentTarget.style.color = '#16A34A'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.color = '#6B7280'; }}
            >
              <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 13, height: 13, flexShrink: 0 }}>
                <path d="M7 2v8m0 0l-3-3m3 3l3-3M2 11.5v.5A1.5 1.5 0 003.5 13.5h7A1.5 1.5 0 0012 12v-.5"/>
              </svg>
              {t("common.btn.install_download_file")}
            </button>
            <button
              onClick={handleSubmitOpenExtension}
              style={{
                flex: 1, padding: '10px 0',
                border: 'none', background: '#22C55E',
                borderRadius: 8, fontSize: 13, fontWeight: 700,
                color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
                transition: 'background 0.12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#16A34A'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#22C55E'; }}
            >
              {t("common.btn.continue")} →
            </button>
          </div>
        </LoadingWrapper>
      </Dialog>

      {/* ── MOBILE ONLY DIALOG ── */}
      <Dialog
        open={openMobileDialog}
        onClose={() => setOpenMobileDialog(false)}
      >
        <DialogTitle>Thông báo</DialogTitle>
        <DialogContent>
          Tính năng này chỉ khả dụng trên máy tính. Vui lòng truy cập bằng PC
          để tiếp tục.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenMobileDialog(false)}
            className={styles.mobileDialogBtn}
          >
            ĐÓNG
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── TOUR ONBOARDING ── */}
      <Tour
        steps={tourConfig}
        isOpen={tourConfig != null}
        onRequestClose={() => setTourConfig(null)}
        closeWithMask={false}
        lastStepNextButton={<span>{t("common.btn.ok")}</span>}
        showNavigationNumber={false}
        showNavigation={true}
        showButtons={true}
        accentColor="#22C55E"
      />
    </>
  );
}
